import React, { useMemo } from 'react';
import './index.scss';
import { useCompareCompetitorsTimeline } from '../../SameStartDate/hooks';
import { formatCompareCompetitorsTimeline } from '../../utils';
import Chart from './chart';
import { getMetricsItemFunc } from '@op/components/FilterCommon/widgets/MetricsSelect/config';
import { PeriodType } from '../../SameStartDate/FilterInfo';
import moment from 'moment';

interface IGameComparisonChart {
  filter: any;
  gameData: any;
  dateType: string;
  metrics: string[];
  color: { [id: string]: string };
  gameInfo: any;
  singleTimeline?: boolean;
  period?: PeriodType;
}
const GameComparisonChart = (props: IGameComparisonChart) => {
  const {
    filter: propsFilter,
    gameData: propsGameData,
    metrics,
    dateType,
    color,
    gameInfo,
    singleTimeline = false,
    period,
  } = props;

  const { filter, gameData } = useMemo(
    () => ({
      filter: {
        ...propsFilter,
      },
      gameData: propsGameData,
    }),
    [propsFilter, propsGameData],
  );

  const {
    data: timelineData,
    loading,
    error,
  } = useCompareCompetitorsTimeline({
    filter,
    formatData: formatCompareCompetitorsTimeline,
    extraData: {
      dateType,
      gameData,
      color,
    },
  });

  const getMetricsItem = useMemo(
    () => getMetricsItemFunc(gameInfo?.entity_type || 'mobile'),
    [gameInfo],
  );

  const formatDate = (date: moment.MomentInput) => moment(date, 'YYYY-MM-DD');

  return (
    <>
      {metrics?.map((val) => {
        const { xAxisData, yAxisData, tableData, length } =
          timelineData[val] || {};
        let { isEmpty = true } = timelineData[val] || {};
        const item = getMetricsItem(val);
        let emptyDescText = undefined;
        let hasEmptyTitle = true;
        let hasEmptyImg = true;
        const { isPeriod, endPeriod } = period || {};

        if (
          isPeriod &&
          dateType === 'daily' &&
          endPeriod &&
          endPeriod < 31 &&
          ['steam_recent_reviews', 'steam_recent_reviews_positive'].includes(
            val,
          )
        ) {
          isEmpty = true;
          emptyDescText =
            '“Recent Reviews” refer to reviews from the past 30 days. Therefore, data is only available starting from the 31st day after launch.';
          hasEmptyTitle = false;
          hasEmptyImg = false;
        }

        if (isEmpty && hasEmptyTitle) {
          if (
            ['steam_recent_reviews', 'steam_recent_reviews_positive'].includes(
              val,
            )
          ) {
            const valid =
              yAxisData?.filter((val) => {
                const { release, end_time } = val;
                return (
                  release &&
                  formatDate(end_time).diff(formatDate(release), 'days') > 30
                );
              }) || [];
            if (valid.length === 0) {
              emptyDescText =
                '“Recent Reviews” refer to reviews from the past 30 days. Therefore, data is only available starting from the 31st day after launch.';
              hasEmptyTitle = false;
              hasEmptyImg = false;
            }
          }

          if (
            ['steam_all_reviews', 'steam_all_reviews_positive'].includes(val)
          ) {
            const valid =
              yAxisData?.filter((val) => {
                const { release, end_time } = val;
                return (
                  release &&
                  formatDate(end_time).isAfter(formatDate(release), 'days')
                );
              }) || [];
            if (valid.length === 0) {
              emptyDescText = 'Data available after launch';
              hasEmptyTitle = false;
              hasEmptyImg = false;
            }
          }
        }

        return (
          <Chart
            key={val}
            id={val}
            loading={loading}
            xAxisData={xAxisData}
            yAxisData={yAxisData}
            gameData={gameData}
            filter={{ ...filter, date_type: dateType, ...gameInfo }}
            item={item}
            tableData={tableData}
            isEmpty={isEmpty}
            axisLength={length}
            valueFormat={item?.format}
            isPercent={item?.isPercent}
            yAxisLimit={item?.yAxisLimit}
            logBtnId={`Comparison Chart`}
            dateType={dateType}
            singleTimeline={singleTimeline}
            period={period}
            hasEmptyTitle={hasEmptyTitle}
            emptyDescText={emptyDescText}
            hasEmptyImg={hasEmptyImg}
          />
        );
      })}
    </>
  );
};

export default GameComparisonChart;
